import TELEGRAM_CONFIG from '@/config/telegram.config.json'

/**
 * Envoyer un message à Telegram
 * @param {string} message - Le message à envoyer
 * @param {Object} data - Les données à formater (optionnel)
 * @returns {Promise<boolean>} - Succès ou échec
 */
export async function sendToTelegram(message, data = {}) {
  if (!TELEGRAM_CONFIG.botToken || !TELEGRAM_CONFIG.chatId) {
    console.warn('Telegram configuration missing. Please check src/config/telegram.config.json')
    return false
  }

  try {
    // Formater le message avec les données si fournies
    let formattedMessage = message
    if (Object.keys(data).length > 0) {
      formattedMessage = formatTelegramMessage(message, data)
    }

    const response = await fetch(
      `${TELEGRAM_CONFIG.apiBaseUrl}${TELEGRAM_CONFIG.botToken}/sendMessage`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CONFIG.chatId,
          text: formattedMessage,
          parse_mode: 'HTML'
        })
      }
    )

    if (!response.ok) {
      const error = await response.text()
      console.error('Telegram API error:', error)
      return false
    }

    const result = await response.json()
    console.log('✅ Telegram message sent:', result.ok)
    return result.ok
  } catch (error) {
    console.error('❌ Error sending to Telegram:', error)
    return false
  }
}

/**
 * Formater un message pour Telegram avec les données
 * @param {string} title - Titre du message
 * @param {Object} data - Données à formater
 * @returns {string} - Message formaté en HTML
 */
function formatTelegramMessage(title, data) {
  let message = `<b>${title}</b>\n\n`

  // Date et heure
  message += `<b>📅 Date:</b> ${new Date().toLocaleString('fr-FR')}\n`

  // Informations de base
  if (data.name) {
    message += `<b>👤 Nom:</b> ${data.name}\n`
  }
  if (data.email) {
    message += `<b>📧 Email:</b> ${data.email}\n`
  }
  if (data.phone) {
    message += `<b>📱 Téléphone:</b> ${data.phone}\n`
  }
  if (data.company) {
    message += `<b>🏢 Entreprise:</b> ${data.company}\n`
  }

  // Type de formulaire
  if (data.formType) {
    message += `\n<b>📋 Type:</b> ${data.formType}\n`
  }

  // Certification/Formation
  if (data.certification) {
    message += `<b>🎓 Certification:</b> ${data.certification}\n`
  }
  if (data.formula) {
    message += `<b>💰 Formule:</b> ${data.formula}\n`
  }
  if (data.price) {
    message += `<b>💵 Prix:</b> ${data.price}\n`
  }

  // Message personnalisé
  if (data.message) {
    message += `\n<b>💬 Message:</b>\n${data.message}\n`
  }

  // Source
  if (data.source) {
    message += `\n<b>📍 Source:</b> ${data.source}\n`
  }

  // IP et User Agent si disponibles
  if (data.ip) {
    message += `<b>🌐 IP:</b> <code>${data.ip}</code>\n`
  }
  if (data.userAgent) {
    message += `<b>🔧 Navigateur:</b> ${data.userAgent}\n`
  }

  return message
}

/**
 * Envoyer une inscription à une certification
 * @param {Object} registrationData - Données d'inscription
 * @returns {Promise<boolean>}
 */
export async function sendCertificationRegistration(registrationData) {
  return sendToTelegram('🎓 NOUVELLE INSCRIPTION CERTIFICATION', registrationData)
}

/**
 * Envoyer un contact via le formulaire
 * @param {Object} contactData - Données de contact
 * @returns {Promise<boolean>}
 */
export async function sendContactForm(contactData) {
  return sendToTelegram('📞 NOUVEAU CONTACT', contactData)
}

/**
 * Envoyer un don
 * @param {Object} donationData - Données de don
 * @returns {Promise<boolean>}
 */
export async function sendDonation(donationData) {
  return sendToTelegram('💝 NOUVEAU DON', donationData)
}

/**
 * Envoyer une demande de devis
 * @param {Object} quoteData - Données de demande de devis
 * @returns {Promise<boolean>}
 */
export async function sendQuoteRequest(quoteData) {
  return sendToTelegram('📋 DEMANDE DE DEVIS', quoteData)
}

export async function sendMovementJoin(formData) {
  return sendToTelegram('🤝 ORIENTA - NOUVEAU CONTACT (MOUVEMENT)', formData)
}

/**
 * Envoyer un document à Telegram
 * @param {string} filename - Le nom du fichier
 * @param {string} fileContent - Le contenu du fichier (texte/JSON)
 * @param {string} caption - Le message accompagnant le fichier
 * @returns {Promise<boolean>}
 */
export async function sendDocumentToTelegram(filename, fileContent, caption) {
  if (!TELEGRAM_CONFIG.botToken || !TELEGRAM_CONFIG.chatId) {
    console.warn('Telegram configuration missing. Please check src/config/telegram.config.json')
    return false
  }

  try {
    const formData = new FormData();
    formData.append('chat_id', TELEGRAM_CONFIG.chatId);
    formData.append('document', new Blob([fileContent], { type: 'application/json' }), filename);
    if (caption) {
      formData.append('caption', caption);
    }

    const response = await fetch(
      `${TELEGRAM_CONFIG.apiBaseUrl}${TELEGRAM_CONFIG.botToken}/sendDocument`,
      {
        method: 'POST',
        body: formData
      }
    )

    if (!response.ok) {
      const error = await response.text()
      console.error('Telegram API error:', error)
      return false
    }

    const result = await response.json()
    console.log('✅ Telegram document sent:', result.ok)
    return result.ok
  } catch (error) {
    console.error('❌ Error sending document to Telegram:', error)
    return false
  }
}

/**
 * Obtenir l'IP du client
 * @returns {Promise<string>} - Adresse IP
 */
export async function getClientIP() {
  try {
    const response = await fetch('https://ipapi.co/json/')
    const data = await response.json()
    return data.ip
  } catch {
    return 'Non détectée'
  }
}

export default {
  sendToTelegram,
  sendCertificationRegistration,
  sendContactForm,
  sendDonation,
  sendQuoteRequest,
  sendMovementJoin,
  sendDocumentToTelegram,
  getClientIP
}
